/**
 * Sales methodology of a CRM pipeline (#766, PR 1).
 * Spec: docs/plans/2026-10-03-deal-state.md §7 (decisions R5, R8, R9, R12).
 *
 * Two layers per pipeline: a standard framework (the base the deal-state
 * recommendations are built on) and the company's own rules on top of it,
 * all optional. No stored row = the default framework, no company rules.
 *
 * Endpoints (edge-api `routes/crm-sales-methodology.ts`):
 * - GET    /api/v1/crm/sales-methodology                          — every stored pipeline of the org
 * - GET    /api/v1/crm/pipelines/:pipelineId/sales-methodology    — one pipeline (default when none stored)
 * - PUT    /api/v1/crm/pipelines/:pipelineId/sales-methodology    — set framework + company rules
 * - DELETE /api/v1/crm/pipelines/:pipelineId/sales-methodology    — back to the default
 */

import { z } from 'zod/v4';
// The leaf entry, not the `@aica/core/voc` barrel: the barrel value-imports
// back into api-types and would close the import cycle (see the file's header).
import { OBJECTION_CODES } from '@aica/core/voc/objection-codes';
import { crmReferenceIdSchema } from './stage-bindings';

export const SALES_FRAMEWORKS = [
  'bant',
  'spin',
  'meddic',
  'challenger',
  'bant_spin',
] as const;

export const salesFrameworkSchema = z.enum(SALES_FRAMEWORKS);
export type SalesFramework = z.infer<typeof salesFrameworkSchema>;

/** What a pipeline without a stored methodology is recommended by (R12). */
export const DEFAULT_SALES_FRAMEWORK: SalesFramework = 'bant_spin';

/** Bounds of the company rules — the route and the form read the same numbers. */
export const COMPANY_RULES_LIMITS = {
  productMax: 2000,
  objectionAnswerMax: 1000,
  stageRuleMax: 500,
  stageRulesMax: 50,
  pauseDaysMin: 1,
  pauseDaysMax: 365,
  prohibitionsMax: 2000,
} as const;

/**
 * Stored methodologies one CRM installation of an organization may hold (#2095:
 * rows are keyed by the install). A CRM has a handful of
 * pipelines; the cap, enforced on write, keeps a made-up `:pipelineId` from
 * growing the table and the list answer without limit (below the cap the id is
 * not checked against the CRM). Rows of pipelines the CRM no longer has do not
 * count when the CRM is readable, but then only a pipeline the CRM has is
 * saved over the cap — and every row, orphans included, stays within
 * `SALES_METHODOLOGY_ROWS_MAX`.
 */
export const SALES_METHODOLOGY_PIPELINES_MAX = 100;

/**
 * The bound on ALL stored rows of one organization, rows of pipelines the CRM
 * no longer has included. Without it the cycle "create a pipeline in the CRM,
 * save its methodology, delete the pipeline" leaves one uncounted row per turn
 * and the list grows without limit. The orphans are the customer's own rules,
 * so the bound refuses a new row instead of deleting old ones: the customer
 * resets them from the orphan list.
 */
export const SALES_METHODOLOGY_ROWS_MAX = SALES_METHODOLOGY_PIPELINES_MAX * 2;

/**
 * Why a save of a NEW pipeline was refused with 409 `too_many_pipelines`
 * (`reason` in the body; the screen words each differently):
 * - `cap_reached` — the pipelines the CRM has fill the cap;
 * - `crm_unavailable` — the cap is met and the CRM list cannot be read (not
 *   connected, processing paused, a failure, empty), so stored rows of vanished
 *   pipelines cannot be told apart and are all counted;
 * - `pipeline_not_in_crm` — the cap is met and the pipeline is not in the CRM;
 * - `too_many_rows` — the stored rows, those of vanished pipelines included,
 *   are at `SALES_METHODOLOGY_ROWS_MAX` (checked first, with no CRM read).
 */
export const salesMethodologyCapRefusalReasonSchema = z.enum([
  'cap_reached',
  'crm_unavailable',
  'pipeline_not_in_crm',
  'too_many_rows',
]);
export type SalesMethodologyCapRefusalReason = z.infer<
  typeof salesMethodologyCapRefusalReasonSchema
>;

/** `:pipelineId` of the per-pipeline routes. */
export const salesMethodologyPipelineParamSchema = z.object({
  pipelineId: crmReferenceIdSchema,
});

const objectionCodeKeySchema = z.enum(OBJECTION_CODES);
const objectionAnswerSchema = z
  .string()
  .trim()
  .min(1)
  .max(COMPANY_RULES_LIMITS.objectionAnswerMax);
const nextStepRuleSchema = z
  .object({
    stageId: crmReferenceIdSchema,
    rule: z.string().trim().min(1).max(COMPANY_RULES_LIMITS.stageRuleMax),
  })
  .strict();
const pauseNormSchema = z
  .object({
    stageId: crmReferenceIdSchema,
    days: z
      .number()
      .int()
      .min(COMPANY_RULES_LIMITS.pauseDaysMin)
      .max(COMPANY_RULES_LIMITS.pauseDaysMax),
  })
  .strict();

/** Rows keyed by a stage: one entry per stage of the pipeline. */
function oneEntryPerStage<T extends { stageId: string }>(
  rows: T[],
  ctx: z.RefinementCtx
): void {
  const seen = new Set<string>();
  rows.forEach((row, index) => {
    if (seen.has(row.stageId)) {
      ctx.addIssue({
        code: 'custom',
        message: 'One entry per stage',
        path: [index, 'stageId'],
      });
    }
    seen.add(row.stageId);
  });
}

/**
 * The company's own rules on top of the framework (spec §7, layer 2). Every
 * field optional; the recommendation takes specifics from here when present.
 */
export const companyRulesSchema = z
  .object({
    /** What is sold, to whom, how it differs. */
    product: z.string().trim().max(COMPANY_RULES_LIMITS.productMax).optional(),
    /** Approved answers by objection reason — the closed set of 19 codes. */
    objectionAnswers: z
      .partialRecord(objectionCodeKeySchema, objectionAnswerSchema)
      .optional(),
    /** "After the proposal — a demo within 2 days", per stage. */
    nextStepRules: z
      .array(nextStepRuleSchema)
      .max(COMPANY_RULES_LIMITS.stageRulesMax)
      .superRefine(oneEntryPerStage)
      .optional(),
    /** The longest acceptable pause in contact, in days, per stage. */
    pauseNorms: z
      .array(pauseNormSchema)
      .max(COMPANY_RULES_LIMITS.stageRulesMax)
      .superRefine(oneEntryPerStage)
      .optional(),
    /** Discounts, promises and the like that must not be offered. */
    prohibitions: z.string().trim().max(COMPANY_RULES_LIMITS.prohibitionsMax).optional(),
  })
  .strict();
export type CompanyRules = z.infer<typeof companyRulesSchema>;

/**
 * Drop what carries nothing — blank texts, empty lists and maps — so "rules
 * filled in" means something was written. `null` when nothing is left: the
 * pipeline then has the framework alone, and the summary does not claim
 * "company rules" it does not have.
 */
export function compactCompanyRules(
  rules: CompanyRules | null | undefined
): CompanyRules | null {
  if (!rules) return null;
  const out: CompanyRules = {};
  if (rules.product) out.product = rules.product;
  if (rules.objectionAnswers && Object.keys(rules.objectionAnswers).length > 0) {
    out.objectionAnswers = rules.objectionAnswers;
  }
  if (rules.nextStepRules && rules.nextStepRules.length > 0) {
    out.nextStepRules = rules.nextStepRules;
  }
  if (rules.pauseNorms && rules.pauseNorms.length > 0) out.pauseNorms = rules.pauseNorms;
  if (rules.prohibitions) out.prohibitions = rules.prohibitions;
  return Object.keys(out).length > 0 ? out : null;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** The items of a per-stage list that pass on their own: first per stage, up to the cap. */
function salvageStageRows<T extends { stageId: string }>(
  raw: unknown,
  itemSchema: z.ZodType<T>
): { rows: T[]; complete: boolean } {
  if (!Array.isArray(raw)) return { rows: [], complete: false };
  const seen = new Set<string>();
  const rows: T[] = [];
  let complete = true;
  for (const item of raw) {
    const parsed = itemSchema.safeParse(item);
    if (
      !parsed.success ||
      seen.has(parsed.data.stageId) ||
      rows.length >= COMPANY_RULES_LIMITS.stageRulesMax
    ) {
      complete = false;
      continue;
    }
    seen.add(parsed.data.stageId);
    rows.push(parsed.data);
  }
  return { rows, complete };
}

/**
 * What of a stored `company_rules_json` is still valid. `companyRulesSchema`
 * is `.strict()` and fails as a whole on one bad part — an objection code
 * removed from `OBJECTION_CODES` later, a limit tightened, a key from a newer
 * build — and a reader that answered `null` for it would have the screen save
 * `null` back and erase every rule. This keeps each field, and each entry of
 * `objectionAnswers` / item of `nextStepRules` / `pauseNorms`, that passes on
 * its own, drops unknown keys, keeps the first item per stage and caps the
 * lists, then compacts. `complete` is false when anything was dropped.
 * `null` (nothing stored) is complete; anything that is not an object — text
 * that was not JSON, an array — yields no rules and is not complete.
 */
export function salvageCompanyRules(raw: unknown): {
  rules: CompanyRules | null;
  complete: boolean;
} {
  if (raw === null) return { rules: null, complete: true };
  const whole = companyRulesSchema.safeParse(raw);
  if (whole.success) return { rules: compactCompanyRules(whole.data), complete: true };
  if (!isPlainObject(raw)) return { rules: null, complete: false };

  let complete = true;
  const out: CompanyRules = {};
  const known = new Set(Object.keys(companyRulesSchema.shape));
  if (Object.keys(raw).some((key) => !known.has(key))) complete = false;

  for (const key of ['product', 'prohibitions'] as const) {
    if (raw[key] === undefined) continue;
    const parsed = companyRulesSchema.shape[key].safeParse(raw[key]);
    if (parsed.success) out[key] = parsed.data;
    else complete = false;
  }

  if (raw.objectionAnswers !== undefined) {
    if (isPlainObject(raw.objectionAnswers)) {
      const answers: NonNullable<CompanyRules['objectionAnswers']> = {};
      for (const [code, text] of Object.entries(raw.objectionAnswers)) {
        const key = objectionCodeKeySchema.safeParse(code);
        const answer = objectionAnswerSchema.safeParse(text);
        if (key.success && answer.success) answers[key.data] = answer.data;
        else complete = false;
      }
      out.objectionAnswers = answers;
    } else {
      complete = false;
    }
  }

  if (raw.nextStepRules !== undefined) {
    const salvaged = salvageStageRows(raw.nextStepRules, nextStepRuleSchema);
    out.nextStepRules = salvaged.rows;
    if (!salvaged.complete) complete = false;
  }
  if (raw.pauseNorms !== undefined) {
    const salvaged = salvageStageRows(raw.pauseNorms, pauseNormSchema);
    out.pauseNorms = salvaged.rows;
    if (!salvaged.complete) complete = false;
  }

  return { rules: compactCompanyRules(out), complete };
}

export const salesMethodologySchema = z.object({
  pipelineId: z.string(),
  framework: salesFrameworkSchema,
  companyRules: companyRulesSchema.nullable(),
  /** No stored row — the default framework, no company rules. */
  isDefault: z.boolean(),
});
export type SalesMethodology = z.infer<typeof salesMethodologySchema>;

/** A pipeline without a stored methodology — the server's answer and the form's starting point. */
export function defaultSalesMethodology(pipelineId: string): SalesMethodology {
  return {
    pipelineId,
    framework: DEFAULT_SALES_FRAMEWORK,
    companyRules: null,
    isDefault: true,
  };
}

export const salesMethodologyResponseSchema = z.object({ data: salesMethodologySchema });
export type SalesMethodologyResponse = z.infer<typeof salesMethodologyResponseSchema>;

/** Stored pipelines only — a pipeline missing here has the default. */
export const salesMethodologyListResponseSchema = z.object({
  data: z.array(salesMethodologySchema),
});
export type SalesMethodologyListResponse = z.infer<
  typeof salesMethodologyListResponseSchema
>;

export const putSalesMethodologyRequestSchema = z
  .object({
    framework: salesFrameworkSchema,
    // Required: a body that leaves the rules out must not read as "clear them".
    companyRules: companyRulesSchema.nullable(),
  })
  .strict();
export type PutSalesMethodologyRequest = z.infer<typeof putSalesMethodologyRequestSchema>;
